(function() {
  "use strict";

  /**
   * Konfigurator Tracking (Analytics wiring only)
   *
   * Purpose:
   * - Observe the configurator lifecycle (open/close, step changes, final step)
   * - Emit analytics events (gtag + dataLayer) without any UI side effects
   * - Provide an optional debug console logger that can be toggled for QA
   *
   * Public API (window.KonfiguratorTracking):
   * - attach(options): Bind observers to a specific configurator root
   * - start(params), exit(params)
   * - startStep(stepNumber, stepName), endStep(completed?)
   * - skipToPersonalize(params), stepProgression(params)
   * - complete(params)
   * - addToCart(params)
   * - companionSelection(params)
   * - sessionEnd(params)
   * - setDebug(enabled), enableDebug(), disableDebug(), isDebug()
   *
   * Debug logging:
   * - Enable via one of:
   *   window.KonfiguratorTracking.enableDebug()
   *   window.KonfiguratorTracking.setDebug(true)
   *   localStorage.setItem('konfiguratorTrackingDebug','1')
   *   window.KonfiguratorTracking.attach({ debug: true })
   * - Disable via disableDebug(), setDebug(false), or remove localStorage key.
   */

  // Lightweight debug logger (no-op in production unless enabled)
  var _debug = {
    enabled: false,
    refreshFromStorage: function() {
      try {
        var v = localStorage.getItem('konfiguratorTrackingDebug');
        _debug.enabled = _debug.enabled || v === '1' || v === 'true';
        // Also allow query param ?konfigdebug=1 (or true) to enable for this load
        try {
          var qp = new URLSearchParams(window.location.search);
          var qv = qp.get('konfigdebug') || qp.get('ktd');
          if (qv && (qv === '1' || qv.toLowerCase() === 'true')) {
            _debug.enabled = true;
          }
        } catch (e) {}
      } catch (e) {}
    },
    set: function(val) {
      _debug.enabled = !!val;
    },
    log: function() {
      if (!_debug.enabled) return;
      try {
        var args = Array.prototype.slice.call(arguments);
        args.unshift('[KonfiguratorTracking]');
        console.log.apply(console, args);
      } catch (e) {}
    }
  };
  // Production default: ensure debug is OFF for all users.
  // Remove any persisted debug flag and do not auto-enable from URL/localStorage.
  try { localStorage.removeItem('konfiguratorTrackingDebug'); } catch (e) {}
  _debug.set(false);

  // Internal ephemeral state for step timing
  var _state = {
    stepStartTime: null,
    currentStepName: null
  };

  // Shared context captured by attach()
  var _ctx = {
    root: null,
    mode: null,
    totalSteps: null,
    personalizeStep: null,
    isGin: false,
    stepNames: null,
    sessionStartTime: null,
    sessionId: null,
    currentStepNumber: null,
    observers: []
  };

  /**
   * Safely proxy to gtag() if available.
   * @param {...any} args
   */
  function safeGtag() {
    if (typeof window.gtag === "function") {
      try { window.gtag.apply(null, arguments); } catch (e) {}
    }
  }

  /**
   * Safely push to dataLayer if available.
   * @param {object} obj
   */
  function pushDataLayer(obj) {
    if (window.dataLayer && typeof window.dataLayer.push === "function") {
      try { window.dataLayer.push(obj); } catch (e) {}
    }
  }

  /**
   * Extract current konfigurator selections from DOM for analytics payloads.
   * @param {HTMLElement} root
   * @param {string} mode - 'gin' | 'vodka'
   * @returns {{recipe:string,juniper:string,alcohol:string,botanical:string,companions:string,mode:string}}
   */
  function getConfigDetails(root, mode) {
    var cfg = {
      recipe: "",
      juniper: "",
      alcohol: "",
      botanical: "",
      companions: "",
      mode: mode
    };
    if (!root || !root.querySelector) return cfg;
    cfg.recipe = (root.querySelector('input[name="properties[Rezept]"]:checked') || {}).value || cfg.recipe;
    cfg.juniper = (root.querySelector('input[name="properties[Wacholdernote]"]:checked') || {}).value || cfg.juniper;
    cfg.alcohol = (root.querySelector('input[name*="Alkohol"]:checked') || {}).value || cfg.alcohol;
    var bot = root.querySelector('input[name*="botanical"]:checked, input[name*="Botanical"]:checked, input[name="properties[Hauptbotanical]"]:checked');
    cfg.botanical = (bot || {}).value || cfg.botanical;
    try {
      cfg.companions = Array.prototype.slice.call(root.querySelectorAll('input[name*="begleiter"]:checked, input[name*="Begleiter"]:checked, input[name="begleiter_option"]:checked')).map(function(el){return el.value;}).join(', ');
    } catch (e) {}
    return cfg;
  }

  window.KonfiguratorTracking = {
    /**
     * Bind tracking observers to a specific konfigurator instance.
     * Options:
     * - root: HTMLElement konfigurator container (optional; auto-detected if omitted)
     * - mode: 'gin'|'vodka'
     * - totalSteps: number of steps in the wizard
     * - personalizeStep: index of personalize step
     * - isGin: boolean hint
     * - stepNames: optional string[] mapping step indices -> names
     * - debug: boolean to enable console logging
     * @param {Object} options
     */
    attach: function(options) {
      options = options || {};
      if (typeof options.debug === 'boolean') {
        _debug.set(!!options.debug);
      }
      _ctx.root = options.root || document.querySelector('.konfigurator-' + (options.mode || '')) || null;
      _ctx.mode = options.mode || null;
      _ctx.totalSteps = options.totalSteps || null;
      _ctx.personalizeStep = options.personalizeStep || null;
      _ctx.isGin = !!options.isGin;
      _ctx.stepNames = options.stepNames || (
        _ctx.isGin ? ['recipe','juniper_alcohol','botanical','companion','personalize']
                   : ['recipe','alcohol','botanical','companion','personalize']
      );
      // Generate a session id for this page view (stable until reload)
      _ctx.sessionId = 'konfig_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

      if (!_ctx.root || !_ctx.mode) return;

      _debug.log('attach()', { mode: _ctx.mode, totalSteps: _ctx.totalSteps, personalizeStep: _ctx.personalizeStep, sessionId: _ctx.sessionId });

      // Observe konfigurator open/close via options container class toggles
      var optionsEl = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-options');
      if (optionsEl) {
        var handleOptions = function() {
          var isActive = optionsEl.classList.contains('active');
          if (isActive) {
            if (!_ctx.sessionStartTime) {
              _ctx.sessionStartTime = Date.now();
              // Fire start only once per session
              window.KonfiguratorTracking.start({ mode: _ctx.mode, sessionId: _ctx.sessionId });
              // Initialize step tracking to current active step
              var activeSection = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-section.active');
              var n = activeSection ? parseInt(activeSection.dataset.section) : 1;
              n = isNaN(n) ? 1 : n;
              _state.stepStartTime = Date.now();
              _state.currentStepName = _ctx.stepNames[n - 1] || '';
              _ctx.currentStepNumber = n;
              _debug.log('session started', { stepNumber: n, stepName: _state.currentStepName });
            }
          } else {
            // Exit
            window.KonfiguratorTracking.exit({ mode: _ctx.mode, exitPoint: 'configuration' });
            _debug.log('session exit');
          }
        };
        var obs = new MutationObserver(handleOptions);
        obs.observe(optionsEl, { attributes: true, attributeFilter: ['class'] });
        _ctx.observers.push(obs);
        // Initial state
        handleOptions();
      }

      // Observe section changes to track step progression and skips
      var sectionsContainer = _ctx.root;
      if (sectionsContainer) {
        var onSectionClassChange = function() {
          var active = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-section.active');
          if (!active) return;
          var nextNum = parseInt(active.dataset.section);
          if (isNaN(nextNum)) return;
          if (_ctx.currentStepNumber === null) {
            _ctx.currentStepNumber = nextNum;
            return; // initial prime handled in options observer
          }
          if (nextNum !== _ctx.currentStepNumber) {
            // Detect skip-to-personalize
            if (_ctx.personalizeStep && _ctx.currentStepNumber === 1 && nextNum === _ctx.personalizeStep) {
              // Label with selected recipe
              var sel = _ctx.root.querySelector('input[name="properties[Rezept]"]:checked');
              var label = sel ? (sel.value || '') : '';
              window.KonfiguratorTracking.skipToPersonalize({ mode: _ctx.mode, label: label });
              _debug.log('skipToPersonalize', { label: label });
            }
            // Close old step and open new
            window.KonfiguratorTracking.endStep(true);
            var fromName = _ctx.stepNames[_ctx.currentStepNumber - 1];
            var toName = _ctx.stepNames[nextNum - 1];
            window.KonfiguratorTracking.startStep(nextNum, toName);
            window.KonfiguratorTracking.stepProgression({
              mode: _ctx.mode,
              fromStep: fromName,
              toStep: toName,
              stepNumber: nextNum,
              progressPercentage: _ctx.totalSteps ? Math.round((nextNum / _ctx.totalSteps) * 100) : undefined
            });
            _debug.log('stepProgression', { from: fromName, to: toName, number: nextNum });
            _ctx.currentStepNumber = nextNum;
          }
        };
        var obs2 = new MutationObserver(onSectionClassChange);
        obs2.observe(sectionsContainer, { attributes: true, subtree: true, attributeFilter: ['class'] });
        _ctx.observers.push(obs2);
      }

      // Detect final step activation to send completion
      var finalEl = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-final-step');
      if (finalEl) {
        var onFinalChange = function() {
          if (finalEl.classList.contains('active')) {
            var details = getConfigDetails(_ctx.root, _ctx.mode);
            window.KonfiguratorTracking.complete({ mode: _ctx.mode, details: details });
            // Also mark last step timing as ended
            window.KonfiguratorTracking.endStep(true);
            _ctx.currentStepNumber = 'final';
            _debug.log('complete', { details: details });
          }
        };
        var obs3 = new MutationObserver(onFinalChange);
        obs3.observe(finalEl, { attributes: true, attributeFilter: ['class'] });
        _ctx.observers.push(obs3);
      }

      // Track cart changes for add_to_cart
      document.addEventListener('cart:change', function(event) {
        try {
          var optionsActive = _ctx.root && _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-options.active');
          if (!optionsActive) return;
          if (!event || !event.detail || event.detail.baseEvent !== 'variant:add') return;
          var items = event.detail.items || [];
          items.forEach(function(item) {
            var props = item.properties || {};
            var hasKonfigProps = props['Rezept'] || props['Recipe'] || props['Alkoholstärke'] || props['Alcohol strength'] || props['Wacholdernote'] || props['Juniper note'];
            if (hasKonfigProps) {
              window.KonfiguratorTracking.addToCart({
                root: _ctx.root,
                mode: _ctx.mode,
                productData: {
                  variant_id: item.variant_id,
                  id: item.id,
                  title: item.title,
                  price: item.price,
                  quantity: item.quantity,
                  properties: props
                }
              });
              _debug.log('addToCart(cart:change)', { variant_id: item.variant_id, title: item.title, price: item.price });
            }
          });
        } catch (e) {}
      });

      // Alternative tracking via product form submission
      document.addEventListener('variant:add', function(event) {
        try {
          var optionsActive = _ctx.root && _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-options.active');
          if (!optionsActive) return;
          if (!event || !event.detail || !event.detail.items) return;
          var form = document.querySelector('form[action*="/cart/add"]');
          var hasKonfigData = false;
          if (form) {
            var fd = new FormData(form);
            hasKonfigData = fd.get('properties[Rezept]') || fd.get('properties[Recipe]') || fd.get('properties[Alkoholstärke]');
          }
          if (hasKonfigData) {
            event.detail.items.forEach(function(item) {
              window.KonfiguratorTracking.addToCart({
                root: _ctx.root,
                mode: _ctx.mode,
                productData: {
                  variant_id: item.variant_id,
                  id: item.id,
                  title: item.title,
                  price: item.price,
                  quantity: item.quantity
                }
              });
              _debug.log('addToCart(variant:add)', { variant_id: item.variant_id, title: item.title, price: item.price });
            });
          }
        } catch (e) {}
      });

      // Track companion selections passively (no UI logic change)
      if (_ctx.root) {
        _ctx.root.addEventListener('change', function(ev) {
          var t = ev.target;
          if (!t || t.type !== 'checkbox') return;
          if (t.name !== 'begleiter_option') return;
          var group = t.closest('.checkbox-group');
          var count = 0;
          try { count = group ? group.querySelectorAll('input[type="checkbox"]:checked').length : 0; } catch (e) {}
          window.KonfiguratorTracking.companionSelection({
            mode: _ctx.mode,
            value: t.value,
            checked: !!t.checked,
            count: count
          });
          _debug.log('companionSelection', { value: t.value, checked: !!t.checked, count: count });
        }, true);
      }

      // beforeunload summary & drop-off
      window.addEventListener('beforeunload', function() {
        if (!_ctx.sessionStartTime) return;
        var totalSessionTime = Math.round((Date.now() - _ctx.sessionStartTime) / 1000);
        var currentStepEl = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-section.active');
        var finalStepReached = _ctx.root.querySelector('.konfigurator-' + _ctx.mode + '-final-step.active');
        var currentStepNumber = currentStepEl ? parseInt(currentStepEl.dataset.section) : 0;
        var dropOffPoint = 'unknown';
        if (finalStepReached) {
          dropOffPoint = 'completed';
        } else if (!isNaN(currentStepNumber) && currentStepNumber > 0 && currentStepNumber <= _ctx.stepNames.length) {
          dropOffPoint = _ctx.stepNames[currentStepNumber - 1];
        }
        window.KonfiguratorTracking.sessionEnd({
          mode: _ctx.mode,
          sessionId: _ctx.sessionId,
          totalSessionTime: totalSessionTime,
          finalStepReached: !!finalStepReached,
          currentStep: currentStepEl ? currentStepEl.dataset.section : null,
          dropOffPoint: dropOffPoint,
          currentStepNumber: currentStepNumber
        });
        _debug.log('sessionEnd', { totalSessionTime: totalSessionTime, finalStepReached: !!finalStepReached, dropOffPoint: dropOffPoint, currentStepNumber: currentStepNumber });
        // Also clear upsell key similar to previous logic
        try {
          var popupShownKey = 'upselling-popup-shown-' + _ctx.mode + '-' + window.location.pathname;
          sessionStorage.removeItem(popupShownKey);
        } catch (e) {}
      });
    },
    /**
     * Start timing for a step.
     * @param {number} stepNumber
     * @param {string} stepName
     */
    startStep: function(stepNumber, stepName) {
      _state.stepStartTime = Date.now();
      _state.currentStepName = stepName;
      _debug.log('startStep', { stepNumber: stepNumber, stepName: stepName });
    },
    /**
     * End timing for the current step.
     * @param {boolean} [completed=true]
     */
    endStep: function(completed) {
      if (completed === void 0) completed = true;
      if (_state.stepStartTime && _state.currentStepName) {
        var timeSpent = Math.round((Date.now() - _state.stepStartTime) / 1000);
        // Keep computed value for potential future use; no direct event here to avoid noise
        void timeSpent;
        _debug.log('endStep', { stepName: _state.currentStepName, timeSpent: timeSpent, completed: completed });
      }
    },

    // Session lifecycle
    /**
     * Session start event (Konfigurator opened).
     * @param {{mode:string, sessionId?:string}} params
     */
    start: function(params) {
      var mode = params && params.mode;
      var sessionId = params && params.sessionId;
      if (!mode) return;
      // Record session start in module state
      _ctx.sessionStartTime = Date.now();
      _ctx.sessionId = sessionId || _ctx.sessionId || ('konfig_' + Date.now());
      safeGtag('event', 'konfigurator_start', {
        event_category: 'Konfigurator',
        event_label: 'StartConfiguration',
        mode: mode,
        custom_mode: mode,
        custom_session_id: _ctx.sessionId || null,
        value: 1
      });
      // Engagement ping (lightweight)
      safeGtag('event', 'engagement', {
        engagement_time_msec: 1000,
        custom_action: 'konfigurator_started',
        custom_mode: mode,
        custom_session_id: _ctx.sessionId || null
      });
      pushDataLayer({
        event: 'konfigurator_start',
        konfigurator_mode: mode,
        konfigurator_session_id: _ctx.sessionId || null,
        konfigurator_started: true
      });
      _debug.log('start', { mode: mode, sessionId: _ctx.sessionId });
    },
    /**
     * Session exit event (Konfigurator closed/collapsed).
     * @param {{mode:string, exitPoint?:string}} params
     */
    exit: function(params) {
      var mode = params && params.mode;
      var exitPoint = params && params.exitPoint || 'configuration';
      if (!mode) return;
      safeGtag('event', 'konfigurator_exit', {
        event_category: 'Konfigurator',
        event_label: 'Exit from Configuration',
        mode: mode,
        custom_mode: mode,
        custom_exit_point: exitPoint
      });
      pushDataLayer({
        event: 'konfigurator_exit',
        konfigurator_mode: mode,
        konfigurator_exit_point: exitPoint
      });
      _debug.log('exit', { exitPoint: exitPoint });
    },

    // Per-step and navigation
    /**
     * User jumped from recipe directly to personalize.
     * @param {{mode:string, label?:string}} params
     */
    skipToPersonalize: function(params) {
      var mode = params && params.mode;
      var label = params && params.label;
      if (!mode) return;
      safeGtag('event', 'konfigurator_skip_to_personalize', {
        event_category: 'Konfigurator',
        event_label: label || '',
        mode: mode
      });
    },
    /**
     * User navigated to the next step in the wizard.
     * @param {{mode:string, fromStep?:string, toStep?:string, stepNumber?:number, progressPercentage?:number}} params
     */
    stepProgression: function(params) {
      var mode = params && params.mode;
      if (!mode) return;
      var fromStep = params.fromStep;
      var toStep = params.toStep;
      var stepNumber = params.stepNumber;
      var progressPercentage = params.progressPercentage;
      safeGtag('event', 'konfigurator_step_progression', {
        event_category: 'Konfigurator',
        event_label: (fromStep || '') + ' → ' + (toStep || ''),
        mode: mode,
        custom_from_step: fromStep,
        custom_to_step: toStep,
        custom_step_number: stepNumber,
        custom_progress_percentage: progressPercentage,
        value: stepNumber
      });
    },

    // Final completion
    /**
     * Final step shown (completed configuration).
     * @param {{mode:string, details?:ReturnType<typeof getConfigDetails>}} params
     */
    complete: function(params) {
      var mode = params && params.mode;
      var details = (params && params.details) || {};
      if (!mode) return;
      var completionData = {
        event_category: 'Konfigurator',
        event_label: 'ConfigurationComplete',
        mode: mode,
        custom_mode: mode,
        custom_recipe: details.recipe || '',
        custom_juniper: details.juniper || '',
        custom_alcohol: details.alcohol || '',
        custom_botanical: details.botanical || '',
        custom_companions: details.companions || '',
        value: 10
      };
      safeGtag('event', 'konfigurator_complete', completionData);
      // GA4 Enhanced Ecommerce view_item
      safeGtag('event', 'view_item', {
        currency: 'EUR',
        value: 0,
        items: [{
          item_name: 'Custom ' + (mode.charAt(0).toUpperCase() + mode.slice(1)),
          item_category: 'Konfigurator',
          item_variant: mode,
          custom_recipe: details.recipe || '',
          custom_alcohol: details.alcohol || '',
          custom_botanical: details.botanical || ''
        }]
      });
      // Google Ads micro-conversion
      safeGtag('event', 'conversion', {
        send_to: 'AW-16754301698/custom_konfigurator',
        value: 5,
        currency: 'EUR'
      });
      pushDataLayer({
        event: 'konfigurator_complete',
        konfigurator_mode: mode,
        konfigurator_recipe: details.recipe || '',
        konfigurator_alcohol: details.alcohol || '',
        konfigurator_botanical: details.botanical || '',
        konfigurator_companions: details.companions || '',
        konfigurator_completed: true
      });
      _debug.log('complete:event', completionData);
    },

    // Add to cart (enhanced ecommerce)
    /**
     * Track add-to-cart originating from the configurator.
     * @param {{root?:HTMLElement, mode:string, productData:Object}} params
     */
    addToCart: function(params) {
      var root = params && params.root;
      var mode = params && params.mode;
      var productData = params && params.productData || {};
      if (!mode) return;

      var configDetails = getConfigDetails(root, mode);
      var ecommerceData = {
        currency: 'EUR',
        value: productData.price ? (productData.price / 100) : 0,
        items: [{
          item_id: productData.variant_id || productData.id,
          item_name: productData.title || 'Konfigurator Product',
          item_category: 'Konfigurator',
          item_variant: mode,
          quantity: productData.quantity || 1,
          price: productData.price ? (productData.price / 100) : 0,
          custom_recipe: configDetails.recipe,
          custom_alcohol: configDetails.alcohol,
          custom_botanical: configDetails.botanical,
          custom_companions: configDetails.companions
        }]
      };
      safeGtag('event', 'add_to_cart', ecommerceData);
      safeGtag('event', 'konfigurator_add_to_cart', {
        event_category: 'Konfigurator',
        event_label: 'Product Added to Cart',
        mode: mode,
        recipe: configDetails.recipe,
        alcohol: configDetails.alcohol,
        botanical: configDetails.botanical,
        companions: configDetails.companions,
        value: ecommerceData.value
      });
      safeGtag('event', 'conversion', {
        send_to: 'AW-16754301698',
        value: ecommerceData.value,
        currency: 'EUR'
      });
      pushDataLayer({
        event: 'konfigurator_add_to_cart',
        konfigurator_mode: mode,
        konfigurator_recipe: configDetails.recipe,
        konfigurator_alcohol: configDetails.alcohol,
        konfigurator_botanical: configDetails.botanical,
        konfigurator_companions: configDetails.companions,
        ecommerce: {
          currency: 'EUR',
          value: ecommerceData.value,
          items: ecommerceData.items
        }
      });
      _debug.log('addToCart:event', { mode: mode, value: ecommerceData.value, variant_id: productData.variant_id || productData.id });
    },

    // Companion selections
    /**
     * Track companion (Begleiter) checkbox selections.
     * @param {{mode:string, value:string, checked:boolean, count:number}} params
     */
    companionSelection: function(params) {
      var mode = params && params.mode;
      var value = params && params.value;
      var checked = !!(params && params.checked);
      var count = params && params.count;
      if (!mode) return;
      safeGtag('event', checked ? 'companion_selected' : 'companion_deselected', {
        event_category: 'Konfigurator',
        event_label: value,
        companion_count: count,
        mode: mode
      });
    },

    // Session end + drop-off
    /**
     * Track session end summary and optional drop-off.
     * @param {{mode:string, sessionId?:string, totalSessionTime:number, finalStepReached:boolean, currentStep?:string, dropOffPoint?:string, currentStepNumber?:number}} params
     */
    sessionEnd: function(params) {
      var mode = params && params.mode;
      if (!mode) return;
      var sessionId = params.sessionId;
      var totalSessionTime = params.totalSessionTime;
      var finalStepReached = !!params.finalStepReached;
      var currentStep = params.currentStep; // optional text id
      var dropOffPoint = params.dropOffPoint;
      var currentStepNumber = params.currentStepNumber;
      safeGtag('event', 'konfigurator_session_end', {
        event_category: 'Konfigurator',
        event_label: 'Session Summary',
        mode: mode,
        custom_mode: mode,
        custom_session_id: sessionId || null,
        custom_session_duration: totalSessionTime,
        custom_final_step_reached: finalStepReached,
        custom_current_step: currentStep || null,
        custom_drop_off_point: dropOffPoint,
        custom_steps_completed: currentStepNumber,
        value: totalSessionTime
      });
      if (!finalStepReached && currentStepNumber > 0) {
        safeGtag('event', 'konfigurator_drop_off', {
          event_category: 'Konfigurator',
          event_label: dropOffPoint,
          mode: mode,
          custom_mode: mode,
          custom_step: dropOffPoint,
          custom_step_number: currentStepNumber,
          custom_session_duration: totalSessionTime,
          value: currentStepNumber
        });
      }
      _debug.log('sessionEnd:event', params);
    },

    /**
     * Enable or disable console debug logging.
     * @param {boolean} enabled
     */
    setDebug: function(enabled) {
      _debug.set(!!enabled);
      try {
        if (enabled) localStorage.setItem('konfiguratorTrackingDebug','1');
        else localStorage.removeItem('konfiguratorTrackingDebug');
      } catch (e) {}
      _debug.log('debug', { enabled: _debug.enabled });
    },
    /** Enable console debug logging */
    enableDebug: function() { this.setDebug(true); },
    /** Disable console debug logging */
    disableDebug: function() { this.setDebug(false); },
    /** Current debug state */
    isDebug: function() { return !!_debug.enabled; }
  };
})();
